// Mock data for Salihi Consulting Services

export const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Services', to: '/services' },
  { label: 'Industries', to: '/industries' },
  { label: 'Clients', to: '/clients' },
  { label: 'Contact', to: '/contact' },
];

export const services = [
  {
    num: '01',
    slug: 'accounting-financial-reporting',
    title: 'Accounting, Bookkeeping & Financial Reporting',
    short: 'End-to-end bookkeeping, ledger management, and IFRS-aligned financial reporting that gives leadership the clarity to act.',
    icon: 'Calculator',
    category: 'Finance',
    long: [
      'Accurate, timely and well-structured financial information sits at the heart of every well-run organization. Our accounting practice provides full-cycle bookkeeping, general ledger management, accounts payable and receivable, bank reconciliations, fixed-asset registers, and management accounts — all delivered with consistency and audit-readiness in mind.',
      'We close monthly and quarterly books on disciplined timelines, issue board-ready management reports, and prepare year-end financial statements aligned with IFRS for SMEs or full IFRS where required. Where clients are scaling, we also design the underlying chart of accounts, costing structures, and reporting cadences so finance becomes a source of decisions rather than friction.',
    ],
    deliverables: [
      'Monthly and quarterly management accounts',
      'Annual financial statements (IFRS-aligned)',
      'Bank, supplier, and intercompany reconciliations',
      'Chart of accounts design & finance policies',
      'Board, investor, and donor reporting packs',
    ],
  },
  {
    num: '02',
    slug: 'tax-advisory-compliance',
    title: 'Tax Registration, Filing, Compliance & Clearance',
    short: 'Tax registration, filing, clearance support, and proactive planning to keep your organization fully compliant and efficient.',
    icon: 'Receipt',
    category: 'Finance',
    long: [
      'Tax obligations in Somalia and the wider East-African region continue to evolve, and getting them wrong is costly. Our tax practice handles the full lifecycle — from initial registration and TIN issuance to ongoing filings, payments, clearance certificates, and dispute support — so management can focus on growth instead of administrative risk.',
      'Beyond compliance, we work with clients on tax-efficient structuring, transaction reviews, withholding tax, payroll taxes, and donor-funded project tax management. Where investigations or assessments arise, we represent and advise clients through reconciliation, objection, and resolution.',
    ],
    deliverables: [
      'Tax registration and TIN issuance',
      'Monthly, quarterly, and annual tax filings',
      'Tax clearance certificate support',
      'Tax-efficient structuring and reviews',
      'Representation in audits and assessments',
    ],
  },
  {
    num: '03',
    slug: 'audit-internal-controls',
    title: 'Financial Auditing, Internal Control Review & Compliance',
    short: 'Independent financial audits, internal-control reviews, and risk assurance engagements delivered with disciplined methodology.',
    icon: 'ShieldCheck',
    category: 'Assurance',
    long: [
      'Stakeholders — boards, investors, donors and regulators — increasingly demand independent assurance over the numbers and the controls behind them. We conduct external audits, internal audits, agreed-upon procedures, and special-purpose reviews scoped tightly to the issue at hand.',
      'Our control reviews diagnose where processes leak value, where fraud risk concentrates, and where governance can be strengthened. Findings are translated into pragmatic remediation roadmaps the organization can actually execute.',
    ],
    deliverables: [
      'External and internal audits',
      'Internal control and risk reviews',
      'Fraud risk assessments',
      'Donor-funded project audits',
      'Remediation roadmaps and follow-up reviews',
    ],
  },
  {
    num: '04',
    slug: 'business-registration-licensing',
    title: 'Business Registration, Licensing & Company Compliance',
    short: 'Company formation, licensing, regulatory filings, and ongoing corporate compliance — done right, the first time.',
    icon: 'FileText',
    category: 'Compliance',
    long: [
      'Establishing a properly registered, licensed and compliant entity is foundational. We guide founders, investors, and international groups through choice of entity, registration with relevant authorities, sector-specific licensing, work permits, and beneficial-ownership filings.',
      'For established companies, we run year-round corporate-secretarial support: statutory filings, board minutes, register maintenance, license renewals, and notifications of structural changes — ensuring the entity remains in good standing at all times.',
    ],
    deliverables: [
      'Company incorporation and registration',
      'Sector and operating licenses',
      'Beneficial ownership and statutory filings',
      'Corporate secretarial and good-standing maintenance',
      'Branch, NGO and representative-office setup',
    ],
  },
  {
    num: '05',
    slug: 'legal-regulatory-advisory',
    title: 'Legal, Regulatory & Corporate Compliance Advisory',
    short: 'Strategic legal, regulatory, and corporate-governance counsel for organizations operating in complex environments.',
    icon: 'Scale',
    category: 'Compliance',
    long: [
      'Operating across jurisdictions — each with its own evolving regulatory expectations — calls for advisors who can translate complex rules into commercial guidance. Our legal and regulatory practice covers commercial contracts, governance frameworks, board policies, AML/CFT compliance, data-protection, and sector-specific regulation.',
      'We design compliance programs that are proportionate, embed them into day-to-day operations, and stand alongside management when regulators, donors or counterparties ask hard questions.',
    ],
    deliverables: [
      'Commercial contract drafting and review',
      'Corporate governance and board frameworks',
      'AML/CFT and compliance programs',
      'Regulatory engagement and licensing strategy',
      'Policy libraries and compliance training',
    ],
  },
  {
    num: '06',
    slug: 'capacity-building-training',
    title: 'Capacity Building, Institutional Development & Staff Training',
    short: 'Institutional development, leadership programs, and bespoke staff training that drives measurable performance gains.',
    icon: 'GraduationCap',
    category: 'Development',
    long: [
      'Strong institutions are built deliberately. We work with ministries, agencies, NGOs and private organizations to strengthen governance, refine operating models, design organizational structures, and embed the systems that allow people to perform.',
      'Our training programs — in finance, leadership, compliance, procurement, M&E and digital skills — are tailored to the actual capability gap, delivered by senior practitioners, and reinforced through coaching and on-the-job application.',
    ],
    deliverables: [
      'Institutional and capacity needs assessments',
      'Operating model and structure design',
      'Custom training curricula and delivery',
      'Leadership development and coaching',
      'Post-training reinforcement and evaluation',
    ],
  },
  {
    num: '07',
    slug: 'gap-assessment-needs-analysis',
    title: 'Gap Assessment, Needs Analysis & Integrated Solutions',
    short: 'Diagnostic reviews and integrated business solutions that pinpoint constraints and unlock pathways to growth.',
    icon: 'Target',
    category: 'Strategy',
    long: [
      'Before solutions, clarity. Our gap-assessment engagements run structured diagnostics across people, process, data, technology, and governance — surfacing where the organization is constrained and which interventions will produce the most leverage.',
      'Findings feed into an integrated solution plan: prioritized, sequenced, costed, and owned. We then walk alongside management through implementation so the assessment does not gather dust.',
    ],
    deliverables: [
      'Operational and functional gap analyses',
      'Capability and capacity assessments',
      'Integrated transformation roadmaps',
      'Implementation planning and sequencing',
      'Quick-win identification and execution',
    ],
  },
  {
    num: '08',
    slug: 'software-erp-it-solutions',
    title: 'Software Development, ERP Customization & IT Solutions',
    short: 'Custom software, ERP customization, and modern IT systems engineered around your operations — not the other way around.',
    icon: 'Code2',
    category: 'Technology',
    long: [
      'Technology should support strategy, not dictate it. Our in-house technology team at Salihi Consulting Services designs, builds and rolls out custom software, web and mobile applications, and customizes ERP platforms so that operations, finance, HR, and reporting flow as a single backbone — delivered directly by us, end-to-end, without outsourcing the core engineering.',
      'We bring engineering discipline — requirements clarity, modular architecture, secure-by-design practices, and post-launch support — to every build, and we transfer ownership to in-house teams so clients are never trapped in vendor lock-in.',
    ],
    deliverables: [
      'Custom software & web/mobile applications',
      'ERP selection, customization & rollout',
      'Systems integration and data migration',
      'Cybersecurity reviews and remediation',
      'Managed support and SLA-based maintenance',
    ],
  },
  {
    num: '09',
    slug: 'investigation-due-diligence',
    title: 'Investigation, Due Diligence, Verification & Compliance Review',
    short: 'Discreet investigations, verification, and compliance reviews that protect your reputation, capital, and decisions.',
    icon: 'Search',
    category: 'Assurance',
    long: [
      'When the stakes are high — an acquisition, a major contract, a partner onboarding, or a suspected internal issue — organizations need facts, not speculation. Our investigation practice runs background checks, integrity due diligence, asset tracing, and compliance reviews with discretion and rigor.',
      'Engagements conclude with a defensible evidence pack and actionable recommendations, enabling boards and executives to act with confidence.',
    ],
    deliverables: [
      'Counterparty and supplier due diligence',
      'Pre-investment / pre-acquisition reviews',
      'Internal investigations and forensic reviews',
      'Sanctions, AML, and integrity screening',
      'Evidence packs and remediation guidance',
    ],
  },
  {
    num: '10',
    slug: 'project-management-execution',
    title: 'Project Management, Implementation Support & Execution',
    short: 'PMO-grade project management, implementation oversight, and execution advisory across the full delivery lifecycle.',
    icon: 'Workflow',
    category: 'Operations',
    long: [
      'Strategy is only as good as execution. We embed senior project managers and PMO capabilities into client teams to plan, govern, and deliver complex initiatives — from system rollouts and organizational change to donor-funded programs and capital projects.',
      'Our approach combines clear governance, transparent reporting, and pragmatic risk management, ensuring milestones are met, budgets respected, and benefits actually realized.',
    ],
    deliverables: [
      'Project charters and governance setup',
      'PMO and program management',
      'Implementation oversight and assurance',
      'Donor-funded project execution support',
      'Benefits realization and close-out reviews',
    ],
  },
  {
    num: '11',
    slug: 'capital-raising-investment',
    title: 'Capital Raising, Investment Readiness & Fundraising Advisory',
    short: 'Investor-ready financials, fundraising strategy, and capital-raising support tailored to ambitious organizations.',
    icon: 'TrendingUp',
    category: 'Finance',
    long: [
      'Whether the goal is equity, debt, blended finance, or grants, the preparation is the same: a credible business case, audit-ready numbers, defensible projections, and a governance posture that institutional capital recognizes.',
      'We prepare clients for capital, refine the investment thesis, build the data room, manage investor engagement, and — where appropriate — introduce qualified investors and development-finance partners from our network.',
    ],
    deliverables: [
      'Investment readiness diagnostics',
      'Information memoranda and pitch decks',
      'Financial models and projections',
      'Data room preparation and management',
      'Investor outreach and process management',
    ],
  },
  {
    num: '12',
    slug: 'corporate-strategy-marketing',
    title: 'Corporate Strategy, Business Advisory & Marketing Support',
    short: 'Strategic planning, business advisory, brand positioning, and go-to-market execution that compounds over time.',
    icon: 'Rocket',
    category: 'Strategy',
    long: [
      'We help shareholders and executives think clearly about where to play and how to win. Engagements range from full strategic plans to focused interventions on growth, pricing, market entry, brand, and customer experience.',
      'Where required, we then support execution — commercial structures, marketing operations, partnerships, and performance tracking — ensuring the strategy moves from document to demonstrable progress.',
    ],
    deliverables: [
      'Strategic plans and growth roadmaps',
      'Market entry and expansion studies',
      'Brand positioning and identity',
      'Marketing operations and campaigns',
      'Performance dashboards and reviews',
    ],
  },
  {
    num: '13',
    slug: 'hr-organizational-development',
    title: 'Human Resources, Organizational Development & Policy',
    short: 'Talent strategy, organizational design, policy development, and HR systems built for sustainable, people-first growth.',
    icon: 'Users',
    category: 'Development',
    long: [
      'People drive performance, and people respond to clarity. We design organizational structures, define roles and grading, build compensation and benefits frameworks, and write the policies and handbooks that translate values into behavior.',
      'On the talent side, we run recruitment, performance management systems, training programs, and HR digitization initiatives — the full stack required to attract, develop, and retain the right people.',
    ],
    deliverables: [
      'Organizational design and job architecture',
      'Compensation, benefits and grading frameworks',
      'HR policies, handbooks and procedures',
      'Recruitment and executive search support',
      'Performance management systems',
    ],
  },
  {
    num: '14',
    slug: 'procurement-supply-chain',
    title: 'Procurement, Supply Chain & Logistics Consulting',
    short: 'Procurement frameworks, supplier strategy, and logistics consulting that cuts cost and elevates resilience.',
    icon: 'Truck',
    category: 'Operations',
    long: [
      'Procurement is one of the largest controllable cost lines for most organizations, and a frequent source of governance risk. We design procurement policies and manuals, run category strategies, structure supplier-evaluation frameworks, and rebuild procure-to-pay workflows.',
      'On the operational side we advise on logistics, warehousing, freight, and supply-chain resilience — reducing cost, lead-time and exposure to single-point-of-failure suppliers.',
    ],
    deliverables: [
      'Procurement policies and manuals',
      'Tendering and supplier evaluation',
      'Category management and sourcing',
      'Procure-to-pay process redesign',
      'Logistics, warehousing and freight advisory',
    ],
  },
  {
    num: '15',
    slug: 'monitoring-evaluation-reporting',
    title: 'Monitoring, Evaluation, Reporting & Documentation Support',
    short: 'M&E frameworks, evidence-based reporting, and documentation support engineered for donor and board confidence.',
    icon: 'ClipboardCheck',
    category: 'Operations',
    long: [
      'Donors, boards, and regulators increasingly demand evidence of impact, not activity. We design results frameworks, theories of change, indicators, and data-collection tools, and we build the dashboards and reporting cadences that turn data into decisions.',
      'Engagements often include baseline, midline, and endline studies, third-party verification, and writing of donor-quality narrative and financial reports.',
    ],
    deliverables: [
      'M&E frameworks and theories of change',
      'Baseline, midline and endline studies',
      'Third-party verification of results',
      'Donor narrative and financial reports',
      'Performance dashboards and visualization',
    ],
  },
];

export const industries = [
  {
    name: 'Government & Public Sector',
    icon: 'Landmark',
    desc: 'Ministries, agencies, and regulatory bodies.',
    long: 'We support ministries, agencies, and regulatory bodies on institutional reform, public financial management, procurement, donor reporting, and digitization programs. Our work has spanned policy formulation, capacity building, and the implementation of operational systems that improve service delivery.',
  },
  {
    name: 'Private Enterprises',
    icon: 'Building2',
    desc: 'SMEs, family businesses, and large corporates.',
    long: 'From founder-led businesses to established conglomerates, we partner with private enterprises on growth strategy, finance transformation, governance, talent, and technology — calibrated to the realities of operating in Somalia and the wider region.',
  },
  {
    name: 'NGOs & Development',
    icon: 'HeartHandshake',
    desc: 'Non-profits, donor-funded programs, and INGOs.',
    long: 'For non-profits, INGOs, and donor-funded programs we deliver compliance, M&E, internal audit, and capacity-building services aligned with major donor requirements, ensuring funds translate into measurable impact.',
  },
  {
    name: 'Banking & Finance',
    icon: 'Banknote',
    desc: 'Banks, microfinance, and fintech operators.',
    long: 'We advise banks, microfinance institutions, and fintech operators on governance, risk and compliance, AML/CFT programs, regulatory engagement, and the design of financial products that meet both market demand and supervisory expectations.',
  },
  {
    name: 'Logistics & Trade',
    icon: 'Ship',
    desc: 'Shipping, freight, supply chain operators.',
    long: 'For logistics, shipping, and trade operators we improve operational efficiency, design controls, support customs and licensing matters, and build the management information needed to make pricing and capacity decisions confidently.',
  },
  {
    name: 'Technology',
    icon: 'Cpu',
    desc: 'Software groups, telecoms, and IT firms.',
    long: 'Technology firms benefit from our integrated finance, compliance, and HR support — freeing founders and product teams to focus on building, while we ensure the back office is investor-ready and operationally sound.',
  },
  {
    name: 'Energy & Industry',
    icon: 'Factory',
    desc: 'Oil, energy, and industrial enterprises.',
    long: 'Energy and industrial clients call on us for project structuring, regulatory advisory, procurement governance, and the kind of robust financial and operational controls that capital-intensive operations demand.',
  },
  {
    name: 'Health & Laboratories',
    icon: 'FlaskConical',
    desc: 'Healthcare providers, labs, and pharma.',
    long: 'Healthcare providers, laboratories, and pharmaceutical operators turn to us for licensing, quality and compliance, finance management, supply-chain support, and the digital systems that underpin safe and efficient operations.',
  },
];

export const clients = [
  { name: 'Ocean Oasis Group', sector: 'Hospitality & Real Estate', desc: 'Advisory and finance support across operations and reporting.' },
  { name: 'African Skies Limited', sector: 'Aviation', desc: 'Compliance and corporate support engagements.' },
  { name: 'OYAK', sector: 'Industrial Conglomerate', desc: 'Strategic and operational advisory.' },
  { name: 'SomTurk PLC', sector: 'Diversified Enterprise', desc: 'Governance, finance, and compliance support.' },
  { name: 'Somali Bureau of Standards', sector: 'Public Institution', desc: 'Institutional development and capacity building.' },
  { name: 'Ministry of Finance', sector: 'Government', desc: 'Public sector advisory and capacity strengthening.' },
  { name: 'Hope Labs Group', sector: 'Healthcare & Laboratories', desc: 'Compliance, finance and operational advisory.' },
  { name: 'Hornlink Limited', sector: 'Trading & Distribution', desc: 'Finance and business advisory.' },
  { name: 'Descon Group', sector: 'Engineering & Construction', desc: 'Project, compliance and finance support.' },
  { name: 'Abayle Logistics & Trading', sector: 'Logistics', desc: 'Operational and compliance advisory.' },
  { name: 'World Bank Group', sector: 'Development Finance', desc: 'Consultancy and implementation support.' },
  { name: 'African Development Bank', sector: 'Development Finance', desc: 'Advisory and program support engagements.' },
];

export const approach = [
  { step: '01', title: 'Discover', desc: 'We immerse ourselves in your context — stakeholders, data, systems, and ambition — to understand the real problem behind the brief.' },
  { step: '02', title: 'Diagnose', desc: 'Rigorous gap analysis, evidence-based benchmarking, and structured frameworks surface the highest-leverage opportunities.' },
  { step: '03', title: 'Design', desc: 'We co-create tailored solutions — financial, operational, technological, or strategic — with measurable success criteria.' },
  { step: '04', title: 'Deliver', desc: 'Hands-on implementation, capacity transfer, and accountability dashboards that ensure outcomes outlast the engagement.' },
];

export const stats = [
  { value: '28', suffix: '+', label: 'Years of combined expertise' },
  { value: '50', suffix: '+', label: 'Clients served regionally' },
  { value: '15', suffix: '', label: 'Specialized service lines' },
  { value: '12', suffix: '+', label: 'Industries supported' },
];

export const whyChooseUs = [
  { title: 'Multidisciplinary Expertise', desc: 'Finance, legal, technology, HR, and operations under one roof — integrated solutions, not siloed advice.', icon: 'Layers' },
  { title: 'Local Insight, Disciplined Practice', desc: 'Deep Somali and East-African market knowledge paired with disciplined, evidence-based engagement methods.', icon: 'Globe' },
  { title: 'Trusted by Institutions', desc: 'From ministries and development banks to private groups — a track record of confidential, high-stakes engagements.', icon: 'Award' },
  { title: 'Outcome-Obsessed Delivery', desc: 'We measure success by impact realized, not slides delivered. Capacity transfer is built into every engagement.', icon: 'Sparkles' },
];

export const values = [
  { title: 'Integrity', desc: 'We operate with absolute discretion and uncompromising professional ethics on every engagement.' },
  { title: 'Excellence', desc: 'We hold ourselves to the highest standard of craft — from financial models to client communication.' },
  { title: 'Partnership', desc: 'We measure our success by the lasting success of our clients, not by the duration of the engagement.' },
  { title: 'Impact', desc: 'We deliver outcomes that can be measured — capital raised, controls strengthened, capacity built, decisions improved.' },
];

export const contact = {
  phone: '+252 77 2007777',
  email: 'info@salihiconsulting.com',
  address: '1st Floor, Blue Building, Opp ABC Centre, Aden Abdulle International Airport, Mogadishu, Somalia',
  hours: 'Sun – Thu · 8:00 AM – 5:00 PM (EAT)',
};

export const brand = {
  name: 'Salihi Consulting',
  fullName: 'Salihi Consulting Services',
  tagline: 'Premium consulting for forward-thinking organizations.',
  logo: 'https://customer-assets.emergentagent.com/job_content-105/artifacts/mvqymylb_salihi_logo_dark_header_white_gold_transparent.png',
};
